"use client";

import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { type PaginationState } from "@hs/ui";
import {
  Avatar,
  Button,
  cn,
  ListBox,
  Pagination,
  Separator,
  Spinner,
  toast,
} from "@heroui/react";
import { DataGrid, InlineSelect, Segment } from "@heroui-pro/react";
import type { DataGridColumn } from "@heroui-pro/react";
import { KPI } from "@heroui-pro/react/kpi";
import { IconChevronLeft, IconDownload as Download } from "@tabler/icons-react";
import { apiClient } from "@hs/shared/utils/api-client";

/** activity.config.statisticalPeriods 项 */
interface StatisticalPeriodItem {
  label: string;
  start_time: string;
  end_time: string;
}

const UNIT_ALL_VALUE = "__all__";
const PERIOD_ALL_VALUE = "__all__";

/** 邀请排行榜单条（与 getInvitationLeaderboard 输出一致） */
type LeaderboardItem = {
  inviterId: string | null;
  inviteCount: number;
  rank: number;
  user: { avatarUrl?: string | null; nickname?: string | null };
  person?: { fullName: string; jobNumber?: string | null } | null;
  org?: { id: string | null; shortName: string } | null;
};

function buildPaginationPages(totalPages: number, safePage: number): (number | "ellipsis")[] {
  const pages: (number | "ellipsis")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push("ellipsis");
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
  }
  return pages;
}

/**
 * 邀请排行榜页面
 * 路由：/activities/:id/leaderboard
 */
export default function InvitationLeaderboardPage() {
  const { id: activityId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: activity, isLoading: activityLoading } = trpc.activities.get.useQuery(
    { id: activityId! },
    { enabled: !!activityId }
  );

  const periods = React.useMemo((): StatisticalPeriodItem[] => {
    const raw = activity?.config as Record<string, unknown> | null | undefined;
    if (!raw || !Array.isArray(raw.statisticalPeriods)) return [];
    return (raw.statisticalPeriods as StatisticalPeriodItem[]).filter(
      (p) => p?.label && p?.start_time && p?.end_time
    );
  }, [activity?.config]);

  const defaultPeriodIndex = React.useMemo(() => {
    const now = Date.now();
    const idx = periods.findIndex((p) => {
      const start = new Date(p.start_time).getTime();
      const end = new Date(p.end_time).getTime();
      return now >= start && now <= end;
    });
    return idx >= 0 ? String(idx) : periods.length > 0 ? "0" : PERIOD_ALL_VALUE;
  }, [periods]);

  const [unitValue, setUnitValue] = React.useState<string>(UNIT_ALL_VALUE);
  const [periodValue, setPeriodValue] = React.useState<string>(defaultPeriodIndex);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [exportLeaderboardLoading, setExportLeaderboardLoading] = React.useState(false);

  React.useEffect(() => {
    setPeriodValue((prev) => {
      if (periods.length === 0) return PERIOD_ALL_VALUE;
      const idx = periods.findIndex((_, i) => String(i) === prev);
      if (idx >= 0) return prev;
      return defaultPeriodIndex;
    });
  }, [periods.length, defaultPeriodIndex]);

  const { data: orgs = [], isLoading: orgsLoading } = trpc.orgs.listAll.useQuery(
    { type: "BRANCH" },
    { enabled: !!activityId }
  );

  const statsParams = React.useMemo(() => {
    const base = {
      activityId: activityId!,
      unitType: unitValue === UNIT_ALL_VALUE ? ("SPACE" as const) : ("ORG" as const),
      ...(unitValue !== UNIT_ALL_VALUE && { unitId: unitValue }),
    };
    const periodIndex =
      periodValue === "" || periodValue === PERIOD_ALL_VALUE ? -1 : parseInt(periodValue, 10);
    if (periods[periodIndex]) {
      const p = periods[periodIndex];
      return {
        ...base,
        startTime: new Date(p.start_time),
        endTime: new Date(p.end_time),
      };
    }
    return base;
  }, [activityId, unitValue, periodValue, periods]);

  const handleExportLeaderboard = React.useCallback(async () => {
    if (!activityId) {
      toast.danger("无法导出：缺少活动 ID");
      return;
    }
    setExportLeaderboardLoading(true);
    try {
      const params: Record<string, string> = {
        activityId,
        unitType: unitValue === UNIT_ALL_VALUE ? "SPACE" : "ORG",
      };
      if (unitValue !== UNIT_ALL_VALUE) params.unitId = unitValue;
      const periodIndex =
        periodValue === "" || periodValue === PERIOD_ALL_VALUE ? -1 : parseInt(periodValue, 10);
      if (periods[periodIndex]) {
        const p = periods[periodIndex];
        params.startTime = new Date(p.start_time).toISOString();
        params.endTime = new Date(p.end_time).toISOString();
      }
      const filename = `${(activity?.title ?? "邀请排行榜").replace(/[/\\?%*:|"]/g, "_")}_邀请排行榜.xlsx`;
      await apiClient.downloadFile(
        "activities/export/invitation-leaderboard",
        params,
        filename
      );
      toast.success("邀请排行榜已开始下载");
    } catch (err) {
      toast.danger(err instanceof Error ? err.message : "导出邀请排行榜失败");
    } finally {
      setExportLeaderboardLoading(false);
    }
  }, [activityId, unitValue, periodValue, periods, activity?.title]);

  const { data: basicStats, isLoading: statsLoading } = trpc.activities.getBasicStats.useQuery(
    statsParams,
    { enabled: !!activityId }
  );

  const { data: leaderboard, isLoading: leaderboardLoading } =
    trpc.activities.getInvitationLeaderboard.useQuery(
      {
        ...statsParams,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
      },
      { enabled: !!activityId }
    );

  const isLoading = activityLoading || orgsLoading || statsLoading;
  const items = leaderboard?.items ?? [];
  const total = leaderboard?.total ?? 0;
  const pageCount = React.useMemo(
    () => Math.max(1, Math.ceil(total / (pagination.pageSize || 20))),
    [total, pagination.pageSize]
  );

  const [countdown, setCountdown] = React.useState<string>("");
  React.useEffect(() => {
    if (!activity?.endTime) {
      setCountdown("");
      return;
    }
    const end =
      typeof activity.endTime === "string" ? new Date(activity.endTime) : activity.endTime;
    const tick = () => {
      const now = new Date();
      const ms = end.getTime() - now.getTime();
      if (ms <= 0) {
        setCountdown("已结束");
        return;
      }
      const d = Math.floor(ms / (24 * 60 * 60 * 1000));
      const h = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const m = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
      const s = Math.floor((ms % (60 * 1000)) / 1000);
      setCountdown(`${d}天${h}时${m}分${s}秒`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activity?.endTime]);

  const columns = React.useMemo<DataGridColumn<LeaderboardItem>[]>(
    () => [
      {
        id: "rank",
        accessorKey: "rank",
        header: "排名",
        width: 72,
        minWidth: 64,
        maxWidth: 88,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="tabular-nums">{row.rank}</span>,
      },
      {
        id: "jobNumber",
        accessorKey: "person",
        header: "工号",
        minWidth: 100,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">{row.person?.jobNumber ?? "–"}</span>
        ),
      },
      {
        id: "profile",
        accessorKey: "inviterId",
        header: "头像 / 姓名",
        minWidth: 200,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => {
          const name = row.person?.fullName ?? row.user?.nickname ?? "未知";
          const initial = name.charAt(0).toUpperCase();
          return (
            <div className="flex min-w-0 items-center gap-2">
              <Avatar className="size-8 shrink-0" variant="soft">
                {row.user?.avatarUrl ? (
                  <Avatar.Image alt="" src={row.user.avatarUrl} />
                ) : null}
                <Avatar.Fallback className="text-sm">{initial}</Avatar.Fallback>
              </Avatar>
              <span className="truncate text-sm">{name}</span>
            </div>
          );
        },
      },
      {
        id: "inviteCount",
        accessorKey: "inviteCount",
        header: "邀请人数",
        width: 100,
        minWidth: 88,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => <span className="tabular-nums">{row.inviteCount}</span>,
      },
      {
        id: "org",
        accessorKey: "org",
        header: "所在组织",
        minWidth: 120,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">{row.org?.shortName ?? "–"}</span>
        ),
      },
    ],
    []
  );

  const columnsWithRowHeader = React.useMemo(() => {
    const first = columns[0];
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
    }));
  }, [columns]);

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = React.useMemo(
    () => buildPaginationPages(pageCount, safePage),
    [pageCount, safePage]
  );

  const pageSizes = [10, 20, 50, 100];

  if (!activityId) {
    return (
      <div className="text-muted-foreground p-4">缺少活动 ID，请从活动列表进入邀请排行榜。</div>
    );
  }

  return (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <header className="border-border flex h-14 shrink-0 items-center border-b px-4">
        <Button isIconOnly variant="tertiary" className="mr-4" onPress={() => navigate("/activities")}>
          <IconChevronLeft className="size-6" stroke={1.5} />
        </Button>
        <h1 className="min-w-0 truncate text-2xl font-bold">
          {`${activity?.title ?? "活动"} · 邀请排行榜`}
        </h1>
      </header>

      <div className="mx-auto flex w-full max-w-full min-h-0 flex-1 flex-col space-y-6 overflow-hidden p-6">
        {isLoading && !activity ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
              <div className="flex min-w-0 max-w-full flex-col gap-2 overflow-x-auto">
                <Segment
                  selectedKey={unitValue}
                  onSelectionChange={(key) => {
                    setUnitValue(String(key));
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                  }}
                >
                  <Segment.Item id={UNIT_ALL_VALUE}>
                    <Segment.Separator />
                    全体
                  </Segment.Item>
                  {orgs.map((org) => {
                    const oid = org.id ?? "";
                    return (
                      <Segment.Item key={org.id ?? oid} id={oid}>
                        <Segment.Separator />
                        {org.shortName ?? org.fullName ?? "未命名"}
                      </Segment.Item>
                    );
                  })}
                </Segment>
              </div>
              <div className="flex min-w-0 max-w-full flex-col gap-2 overflow-x-auto">
                <Segment
                  selectedKey={periodValue}
                  onSelectionChange={(key) => {
                    setPeriodValue(String(key));
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                  }}
                >
                  <Segment.Item id={PERIOD_ALL_VALUE}>
                    <Segment.Separator />
                    不限周期
                  </Segment.Item>
                  {periods.map((p, i) => (
                    <Segment.Item key={i} id={String(i)}>
                      <Segment.Separator />
                      {p.label}
                    </Segment.Item>
                  ))}
                </Segment>
              </div>
              <div className="flex shrink-0 sm:ml-auto">
                <Button
                  variant="secondary"
                  isDisabled={exportLeaderboardLoading}
                  onPress={() => void handleExportLeaderboard()}
                >
                  <Download
                    className={`mr-1 h-4 w-4 ${exportLeaderboardLoading ? "animate-pulse" : ""}`}
                    stroke={1.5}
                  />
                  {exportLeaderboardLoading ? "导出中…" : "导出"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPI className="h-full min-h-[5.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-muted-foreground text-sm">累计邀请（人）</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  {basicStats?.participantCount != null ? (
                    <KPI.Value
                      className="text-lg font-semibold text-foreground"
                      maximumFractionDigits={0}
                      style="decimal"
                      value={basicStats.participantCount}
                    />
                  ) : (
                    <span className="text-lg font-semibold text-muted-foreground">–</span>
                  )}
                </KPI.Content>
              </KPI>
              <KPI className="h-full min-h-[5.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-muted-foreground text-sm">活跃伙伴（人）</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  {basicStats?.inviterCount != null ? (
                    <KPI.Value
                      className="text-lg font-semibold text-foreground"
                      maximumFractionDigits={0}
                      style="decimal"
                      value={basicStats.inviterCount}
                    />
                  ) : (
                    <span className="text-lg font-semibold text-muted-foreground">–</span>
                  )}
                </KPI.Content>
              </KPI>
              <KPI className="h-full min-h-[5.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-muted-foreground text-sm">人均邀请（人）</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  {basicStats?.averageInvites != null ? (
                    <KPI.Value
                      className="text-lg font-semibold text-foreground"
                      maximumFractionDigits={0}
                      style="decimal"
                      value={basicStats.averageInvites}
                    />
                  ) : (
                    <span className="text-lg font-semibold text-muted-foreground">–</span>
                  )}
                </KPI.Content>
              </KPI>
              <KPI className="h-full min-h-[5.5rem]">
                <KPI.Header>
                  <KPI.Title className="text-muted-foreground text-sm">倒计时</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  <span className="text-lg font-medium text-foreground tabular-nums">
                    {countdown || "–"}
                  </span>
                </KPI.Content>
              </KPI>
            </div>

            <section
              className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden"
              data-slot="leaderboard-grid"
            >
              {total > 0 || leaderboardLoading ? (
                <div
                  className={cn(
                    "grid min-h-0 min-w-0 w-full flex-1 gap-y-3",
                    "grid-rows-[minmax(0,1fr)_auto]"
                  )}
                >
                  <div className="relative min-h-0 min-w-0 overflow-hidden">
                    <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                      {leaderboardLoading ? (
                        <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                          <Spinner className="h-8 w-8" />
                        </div>
                      ) : null}
                      <DataGrid<LeaderboardItem>
                        key={`${unitValue}\u001e${periodValue}\u001e${pagination.pageSize}`}
                        allowsColumnResize
                        aria-label="邀请排行榜"
                        className="flex h-full min-h-0 min-w-0 flex-col !overflow-visible"
                        columns={columnsWithRowHeader}
                        contentClassName="min-w-0 !overflow-visible"
                        data={items}
                        getRowId={(row) => String(row.inviterId ?? row.rank)}
                        renderEmptyState={() => (
                          <div className="text-muted-foreground py-8 text-center text-sm">
                            本页暂无数据
                          </div>
                        )}
                        scrollContainerClassName={cn(
                          "h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain",
                          "[&_[data-slot=table-resizable-container]]:overflow-visible",
                          "[&_[data-slot=table-content]]:!overflow-visible",
                          "[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20",
                          "[&_th]:min-w-0 [&_td]:min-w-0",
                          "[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0"
                        )}
                        selectionMode="none"
                        showSelectionCheckboxes={false}
                        variant="primary"
                      />
                    </div>
                  </div>

                  {total > 0 ? (
                    <div
                      className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm"
                      data-slot="leaderboard-pagination"
                    >
                      <div className="flex w-fit shrink-0 items-center">
                        <Pagination className="w-fit max-w-none" size="sm">
                          <Pagination.Content className="flex w-fit min-w-0">
                            <Pagination.Item>
                              <Pagination.Previous
                                isDisabled={safePage <= 1}
                                onPress={() =>
                                  setPagination((p) => ({
                                    ...p,
                                    pageIndex: Math.max(0, p.pageIndex - 1),
                                  }))
                                }
                              >
                                <Pagination.PreviousIcon />
                              </Pagination.Previous>
                            </Pagination.Item>
                            {paginationPages.map((p, i) =>
                              p === "ellipsis" ? (
                                <Pagination.Item key={`e-${i}`}>
                                  <Pagination.Ellipsis />
                                </Pagination.Item>
                              ) : (
                                <Pagination.Item key={p}>
                                  <Pagination.Link
                                    isActive={p === safePage}
                                    onPress={() =>
                                      setPagination((prev) => ({
                                        ...prev,
                                        pageIndex: (p as number) - 1,
                                      }))
                                    }
                                  >
                                    {p}
                                  </Pagination.Link>
                                </Pagination.Item>
                              )
                            )}
                            <Pagination.Item>
                              <Pagination.Next
                                isDisabled={safePage >= pageCount}
                                onPress={() =>
                                  setPagination((p) => ({
                                    ...p,
                                    pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                                  }))
                                }
                              >
                                <Pagination.NextIcon />
                              </Pagination.Next>
                            </Pagination.Item>
                          </Pagination.Content>
                        </Pagination>
                      </div>

                      <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                        <InlineSelect
                          aria-label="每页条数"
                          value={String(pagination.pageSize)}
                          onChange={(v) => {
                            if (v) {
                              setPagination((p) => ({
                                ...p,
                                pageSize: Number(v),
                                pageIndex: 0,
                              }));
                            }
                          }}
                        >
                          <InlineSelect.Trigger>
                            <span className="text-muted">每页</span>
                            <InlineSelect.Value />
                            <InlineSelect.Indicator />
                          </InlineSelect.Trigger>
                          <InlineSelect.Popover className="w-[88px]">
                            <ListBox>
                              {pageSizes.map((n) => (
                                <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                                  {n}
                                  <ListBox.ItemIndicator />
                                </ListBox.Item>
                              ))}
                            </ListBox>
                          </InlineSelect.Popover>
                        </InlineSelect>
                        <Separator className="!h-4 shrink-0" orientation="vertical" />
                        <span className="text-muted shrink-0">
                          第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                          {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-muted-foreground rounded-md border py-8 text-center text-sm">
                  暂无排行榜数据
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
